/**
 * visualizer-bridge.js — Make Winamp's oscilloscope / spectrum analyser work
 * with Apple Music playback.
 *
 * The problem:
 *   Webamp's visualizer reads from an AnalyserNode that is connected to the
 *   (muted, silent) patched audio element. MusicKit plays through a separate
 *   DRM-protected audio pipeline, so the analyser sees flat silence.
 *
 * Two-tier solution:
 *
 *   Tier 1 — Tab audio capture (REAL data):
 *     navigator.mediaDevices.getDisplayMedia({ audio: true }) captures the
 *     tab's entire audio output, including MusicKit's decrypted playback.
 *     We feed that stream into a MediaStreamSourceNode connected to Webamp's
 *     own AnalyserNode. The visualizer then reacts to the actual music.
 *     Requires one user gesture ("Share this tab").
 *
 *   Tier 2 — Synthetic fallback (always on):
 *     Override getByteFrequencyData / getByteTimeDomainData on the analyser
 *     instance with functions that generate music-like waveforms driven by
 *     the MusicKit timeupdate clock. Looks believable; requires no permissions.
 *     Active by default; deactivated when real capture is running; restored
 *     automatically if the user stops sharing.
 *
 * Usage:
 *   const vizBridge = new VisualizerBridge(webamp, mkBridge);
 *   await vizBridge.init();                   // starts synthetic immediately
 *   await vizBridge.requestTabCapture();      // upgrades to real data
 */

// ─── Gaussian helper ────────────────────────────────────────────────────────
function gauss(x, mean, sigma) {
  return Math.exp(-0.5 * ((x - mean) / sigma) ** 2);
}

// ─────────────────────────────────────────────────────────────────────────────

export class VisualizerBridge {
  constructor(webamp, mkBridge) {
    this.webamp = webamp;
    this.mk     = mkBridge;

    this._analyser     = null;
    this._audioContext = null;
    this._streamSource    = null;  // MediaStreamSourceNode from tab capture
    this._measureAnalyser = null;  // side-channel AnalyserNode (not wired to speakers)

    this._syntheticActive = false;
    this._origFreq  = null;   // saved original getByteFrequencyData
    this._origTime  = null;   // saved original getByteTimeDomainData

    this._isPlaying    = false;
    this._playbackTime = 0;

    // Spotify audio intercept — patched into AudioNode.prototype.connect
    this._interceptInstalled = false;
    this._spotifyAnalyser    = null; // AnalyserNode inside Spotify's AudioContext

    // Callback fired when capture state changes so callers can update UI
    this.onStateChange = null; // (state: 'synthetic' | 'live' | 'off') => void
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  /**
   * Find Webamp's analyser, start synthetic visualization, wire MusicKit events.
   * Safe to call multiple times.
   */
  async init() {
    const found = await this._findAnalyser();
    if (!found) {
      console.warn('[VisualizerBridge] Could not find Webamp AnalyserNode — visualizer inactive.');
      this._emitState('off');
      return;
    }

    // Browsers suspend AudioContext until a user gesture.
    // We'll resume it lazily on first click anywhere.
    if (this._audioContext.state === 'suspended') {
      document.addEventListener('click', () => {
        this._audioContext.resume().catch(() => {});
      }, { once: true });
    }

    // Start synthetic immediately so the visualizer isn't dark by default.
    this._startSynthetic();

    // Install global audio interceptor so Spotify's Web Playback SDK audio
    // is captured automatically — no tab sharing required.
    this._installAudioInterceptor();

    // Track MusicKit playback state for synthetic animation.
    this.mk.addEventListener('playstate', (e) => {
      this._isPlaying = e.detail?.isPlaying ?? false;
    });
    this.mk.addEventListener('timeupdate', (e) => {
      this._playbackTime = e.detail?.currentTime ?? 0;
    });
  }

  /**
   * Switch the active bridge (called when the user switches between Apple Music
   * and Spotify). Updates which bridge drives the synthetic visualizer fallback.
   */
  setBridge(newBridge) {
    this.mk = newBridge;
  }

  /**
   * Deactivate any external (Spotify) analyser and revert to synthetic viz.
   * Call this when switching away from Spotify.
   */
  deactivateExternalAnalyser() {
    if (this._streamSource) return; // tab capture is active — leave it alone
    if (!this._spotifyAnalyser) return;
    this._stopSynthetic();
    this._startSynthetic();
    this._emitState('synthetic');
  }

  /**
   * Request tab audio capture.  Upgrades the visualizer from synthetic to real.
   * Returns true on success, false if the user denied or the API is unavailable.
   */
  async requestTabCapture() {
    if (!this._analyser) {
      console.warn('[VisualizerBridge] init() not complete yet.');
      return false;
    }

    try {
      // Ensure AudioContext is running before connecting the stream.
      if (this._audioContext.state === 'suspended') {
        await this._audioContext.resume();
      }

      // Chrome 107+ supports video:false. Older browsers need video:true.
      let stream;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: false,
        });
      } catch {
        // Fallback: request with video, then immediately stop the video track.
        stream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: { displaySurface: 'browser' },
        });
        stream.getVideoTracks().forEach(t => t.stop());
      }

      const audioTracks = stream.getAudioTracks();
      if (!audioTracks.length) {
        console.warn('[VisualizerBridge] Capture stream has no audio track.');
        return false;
      }

      // ── IMPORTANT: do NOT connect the stream into Webamp's audio graph ──
      // Connecting to _preamp or _analyser routes audio to the speakers,
      // causing a double-playback echo/static on top of MusicKit's output.
      //
      // Instead: create a silent side-channel AnalyserNode (not connected
      // to AudioContext.destination), feed the stream into it, then override
      // Webamp's analyser read-methods to delegate to this side-channel node.
      // Real frequency data flows to the visualizer; nothing reaches speakers.

      this._streamSource?.disconnect();
      this._measureAnalyser?.disconnect();

      // Side-channel analyser — intentionally a dead end (no .connect() call)
      this._measureAnalyser = this._audioContext.createAnalyser();
      this._measureAnalyser.fftSize           = this._analyser.fftSize;
      this._measureAnalyser.smoothingTimeConstant = this._analyser.smoothingTimeConstant;

      this._streamSource = this._audioContext.createMediaStreamSource(stream);
      this._streamSource.connect(this._measureAnalyser);
      // ← _measureAnalyser is NOT connected to anything further

      // Override the real analyser's read methods to pull from the side-channel.
      this._stopSynthetic(); // remove any synthetic override first
      const measure = this._measureAnalyser;
      this._analyser.getByteFrequencyData  = (arr) => measure.getByteFrequencyData(arr);
      this._analyser.getByteTimeDomainData = (arr) => measure.getByteTimeDomainData(arr);
      this._syntheticActive = true; // reuse the flag so _stopSynthetic() cleans this up too
      this._origFreq = AnalyserNode.prototype.getByteFrequencyData.bind(this._analyser);
      this._origTime = AnalyserNode.prototype.getByteTimeDomainData.bind(this._analyser);

      this._emitState('live');

      // If the user clicks "Stop sharing", fall back to synthetic.
      audioTracks[0].addEventListener('ended', () => {
        console.log('[VisualizerBridge] Tab capture ended, reverting to synthetic.');
        this._streamSource?.disconnect();
        this._measureAnalyser?.disconnect();
        this._streamSource    = null;
        this._measureAnalyser = null;
        this._stopSynthetic();
        this._startSynthetic();
        this._emitState('synthetic');
      });

      return true;

    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        console.warn('[VisualizerBridge] getDisplayMedia error:', err.message);
      }
      return false;
    }
  }

  /** Current state: 'live' | 'synthetic' | 'off' */
  get state() {
    if (!this._analyser) return 'off';
    return this._streamSource ? 'live' : (this._syntheticActive ? 'synthetic' : 'off');
  }

  // ─────────────────────────────────────────────────────────────
  // Spotify audio interceptor
  // ─────────────────────────────────────────────────────────────

  /**
   * Patch AudioNode.prototype.connect globally (once) so that when the Spotify
   * Web Playback SDK connects its audio output to its AudioContext's destination,
   * we intercept it and route through our own AnalyserNode first.
   *
   * We skip Webamp's own AudioContext so its graph is unaffected.
   */
  _installAudioInterceptor() {
    if (this._interceptInstalled) return;
    this._interceptInstalled = true;

    const webampCtx = this._audioContext;
    const self = this;
    const origConnect = AudioNode.prototype.connect;

    AudioNode.prototype.connect = function (destination, outputIndex, inputIndex) {
      // Only intercept connections from a non-Webamp context going to its destination
      if (
        this.context !== webampCtx &&
        destination instanceof AudioDestinationNode
      ) {
        const ctx = this.context;

        // Create (or reuse) an analyser inside Spotify's AudioContext
        if (!self._spotifyAnalyser || self._spotifyAnalyser.context !== ctx) {
          const analyser = ctx.createAnalyser();
          analyser.fftSize              = 2048;
          analyser.smoothingTimeConstant = 0.8;
          // Connect analyser → real destination so audio still plays
          origConnect.call(analyser, destination, 0, inputIndex);
          self._spotifyAnalyser = analyser;
          // Switch the visualizer to read from this analyser
          self._activateExternalAnalyser(analyser);
          console.log('[VisualizerBridge] Spotify audio intercepted ✓');
        }

        // Route this node → our analyser instead of directly to destination
        return origConnect.call(this, self._spotifyAnalyser, outputIndex ?? 0, 0);
      }

      return origConnect.apply(this, arguments);
    };
  }

  /**
   * Point Webamp's analyser read-methods at an external AnalyserNode so the
   * visualizer reacts to real audio from that node (e.g. Spotify's context).
   */
  _activateExternalAnalyser(extAnalyser) {
    if (this._streamSource) return; // tab capture takes priority
    this._stopSynthetic();

    const measure = extAnalyser;
    this._analyser.getByteFrequencyData  = (arr) => measure.getByteFrequencyData(arr);
    this._analyser.getByteTimeDomainData = (arr) => measure.getByteTimeDomainData(arr);

    // Reuse _syntheticActive flag so _stopSynthetic() will clean these up
    this._syntheticActive = true;
    this._origFreq = AnalyserNode.prototype.getByteFrequencyData.bind(this._analyser);
    this._origTime = AnalyserNode.prototype.getByteTimeDomainData.bind(this._analyser);

    this._emitState('live');
  }

  // ─────────────────────────────────────────────────────────────
  // Analyser discovery
  // ─────────────────────────────────────────────────────────────

  _findAnalyser() {
    return new Promise(resolve => {
      let attempts = 0;

      const tryFind = () => {
        const media = this.webamp.media;

        // Primary path (confirmed via live inspection):
        //   webamp.media._analyser  — AnalyserNode
        //   webamp.media._context   — AudioContext
        // The graph is: MediaElementSource → EQ bands → preamp → _analyser → destination
        const analyser =
          media?._analyser       ?? media?._analyzerNode  ??
          // Fallback: older Webamp versions stored it on _source
          media?._source?._analyser ?? media?._source?._analyzerNode;

        const context =
          media?._context        ?? media?._audioContext  ??
          media?._source?._context ?? media?._source?._audioContext;

        if (analyser instanceof AnalyserNode && context instanceof AudioContext) {
          this._analyser     = analyser;
          this._audioContext = context;
          console.log('[VisualizerBridge] AnalyserNode found ✓', analyser);
          resolve(true);
          return;
        }

        // Retry for up to 10 seconds (Webamp initialises asynchronously).
        if (++attempts > 100) {
          console.warn('[VisualizerBridge] AnalyserNode not found after 10s.');
          resolve(false);
          return;
        }
        setTimeout(tryFind, 100);
      };

      tryFind();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Synthetic visualizer
  // ─────────────────────────────────────────────────────────────

  _startSynthetic() {
    if (this._syntheticActive) return;
    this._syntheticActive = true;

    const self = this;
    const node = this._analyser;

    // Save originals so we can restore them later.
    this._origFreq = node.getByteFrequencyData.bind(node);
    this._origTime = node.getByteTimeDomainData.bind(node);

    node.getByteFrequencyData = function (array) {
      // Read isPlaying directly from MusicKit — don't rely on cached event state
      // which may lag or be missed if the event fires before our listener is set up.
      const playing = self.mk.isPlaying ?? self._isPlaying;
      if (!playing) { array.fill(0); return; }
      // Use performance.now() for smooth continuous animation independent of
      // timeupdate (which only fires every ~250 ms).
      self._fillFrequencyData(array, performance.now() / 1000);
    };

    node.getByteTimeDomainData = function (array) {
      const playing = self.mk.isPlaying ?? self._isPlaying;
      if (!playing) { array.fill(128); return; }
      self._fillTimeDomainData(array, performance.now() / 1000);
    };
  }

  _stopSynthetic() {
    if (!this._syntheticActive) return;
    this._syntheticActive = false;

    const node = this._analyser;
    if (this._origFreq) { node.getByteFrequencyData  = this._origFreq; this._origFreq = null; }
    if (this._origTime) { node.getByteTimeDomainData = this._origTime; this._origTime = null; }
  }

  // ─────────────────────────────────────────────────────────────
  // Synthetic data generators
  // ─────────────────────────────────────────────────────────────

  /**
   * Generate frequency-domain data (for the spectrum analyser bar display).
   *
   * Design goals:
   *  - Wide dynamic range: bass pumps 60–255 on beat, highs sit 10–60
   *  - Proper attack/decay envelopes on kick and snare (not sin^N)
   *  - Overall energy breathes slowly so it never feels frozen
   *  - All contributions are normalised 0–1 and scaled once at the end
   */
  _fillFrequencyData(array, t) {
    const len = array.length;

    // ── Beat clock (120 BPM = 2 Hz) ─────────────────────────────
    const beatPhase  = (t * 2) % 1;              // 0→1 per beat
    const snarePhase = (t * 2 + 0.5) % 1;        // off-beat

    // Sharp attack, exponential decay — this is what makes bars pump.
    const kick  = Math.exp(-beatPhase  * 9);      // 1→0 over ~0.5 s
    const snare = Math.exp(-snarePhase * 11) * 0.65;

    // Slow-breathing overall energy (period ~2.8 s, range 0.35–0.85).
    const energy = 0.60 + 0.25 * Math.sin(t * 2.24) + 0.08 * Math.sin(t * 5.31);

    for (let i = 0; i < len; i++) {
      const norm = i / len;   // 0 = DC / bass, 1 = Nyquist / treble

      // ── Static spectral shape ──────────────────────────────────
      // Strong bass roll-off so highs are naturally much quieter.
      const shape = Math.pow(1 - norm, 2.2) * 0.55;

      // Slow-drifting resonant peaks (small amplitude — colour, not bulk).
      const p1 = gauss(norm, 0.06 + 0.012 * Math.sin(t * 0.37), 0.016) * 0.55;
      const p2 = gauss(norm, 0.18 + 0.020 * Math.sin(t * 0.61), 0.022) * 0.32;
      const p3 = gauss(norm, 0.36 + 0.030 * Math.cos(t * 0.47), 0.026) * 0.20;
      const p4 = gauss(norm, 0.58 + 0.018 * Math.sin(t * 0.29), 0.030) * 0.11;

      // ── Percussion ────────────────────────────────────────────
      // Kick: concentrated in bass, bleeds gently into mids.
      const kickAmt  = kick  * Math.exp(-norm * 7) * 0.85;

      // Snare: centred in upper-mids (norm ≈ 0.35).
      const snareAmt = snare * gauss(norm, 0.35, 0.18) * 0.55;

      // Hi-hat: only high bins, very brief flicker.
      const hihat = norm > 0.62
        ? Math.max(0, Math.sin(t * 18 + norm * 25) ** 8) * 0.18
        : 0;

      // ── Noise floor (tiny) ─────────────────────────────────────
      const noise = Math.random() * 0.025 * (1 - norm * 0.6);

      // ── Combine & scale ───────────────────────────────────────
      const raw = (shape + p1 + p2 + p3 + p4 + kickAmt + snareAmt + hihat + noise) * energy;
      array[i] = Math.min(255, Math.max(0, Math.round(raw * 240)));
    }
  }

  /**
   * Generate time-domain data (for the oscilloscope waveform display).
   * Amplitude pulses with the kick so the waveform visibly breathes.
   */
  _fillTimeDomainData(array, t) {
    const len = array.length;

    // Amplitude envelope: swells on the beat (2 Hz), decays between.
    const beatPhase = (t * 2) % 1;
    const beatAmp   = 0.45 + 0.55 * Math.exp(-beatPhase * 7);

    for (let i = 0; i < len; i++) {
      const x = i / len;

      // Fundamental + harmonics — different rates so it never looks periodic.
      const wave =
        Math.sin(x * Math.PI * 6  + t * 5.31) * 0.40 +
        Math.sin(x * Math.PI * 13 + t * 2.83) * 0.20 +
        Math.sin(x * Math.PI * 3  + t * 9.17) * 0.18 +
        Math.sin(x * Math.PI * 23 + t * 1.17) * 0.08 +
        Math.sin(x * Math.PI * 1  + t * 0.43) * 0.10 +
        (Math.random() - 0.5) * 0.04;

      array[i] = Math.round(128 + wave * beatAmp * 90);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  _emitState(state) {
    this.onStateChange?.(state);
  }
}
